/**
 * InteractiveZoneChart - Full-featured interactive pitch location visualization
 * 
 * Features:
 * - Scatter plot mode with individual pitch dots (real pX/pZ coordinates)
 * - Heatmap mode with hot/cold zones (zone-based aggregates)
 * - Pitch type toggle chips to show/hide specific pitch types
 * - Handedness filter (vs LHB / vs RHB)
 * - Result filter (all / swinging strikes / called strikes / in play / balls)
 * - At-bat result overlay (show only pitches that ended in HR, K, hit, etc.)
 * - Game selector dropdown
 * - Tooltip on hover showing pitch details
 * - Animated transitions between modes
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, ToggleButton, ToggleButtonGroup, Tooltip,
  FormControl, Select, MenuItem, InputLabel, Switch, FormControlLabel,
  alpha, Fade, Paper, useTheme, IconButton, Stack
} from '@mui/material';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import GridOnIcon from '@mui/icons-material/GridOn';
import FilterListIcon from '@mui/icons-material/FilterList';
import { PITCH_NAMES as PITCH_TYPE_NAMES, PITCH_COLORS } from '../../utils/pitchTypes';

// ────────── Constants ──────────

const RESULT_FILTERS = [
  { key: 'all', label: 'All', color: '#888' },
  { key: 'swstr', label: 'Swinging Strike', color: '#e74c3c' },
  { key: 'called', label: 'Called Strike', color: '#f39c12' },
  { key: 'foul', label: 'Foul', color: '#9b59b6' },
  { key: 'ball', label: 'Ball', color: '#3498db' },
  { key: 'inplay', label: 'In Play', color: '#2ecc71' },
];

const AB_RESULT_FILTERS = [
  { key: 'all', label: 'All ABs' },
  { key: 'home_run', label: 'Home Runs' },
  { key: 'strikeout', label: 'Strikeouts' },
  { key: 'single', label: 'Singles' },
  { key: 'double', label: 'Doubles' },
  { key: 'triple', label: 'Triples' },
  { key: 'walk', label: 'Walks' },
  { key: 'hit', label: 'All Hits' },
];

const HEATMAP_COLORS = [
  'rgba(33, 150, 243, 0.15)', 'rgba(33, 150, 243, 0.40)',
  'rgba(76, 175, 80, 0.45)', 'rgba(255, 235, 59, 0.55)',
  'rgba(255, 152, 0, 0.65)', 'rgba(244, 67, 54, 0.75)',
  'rgba(211, 47, 47, 0.90)',
];

const getHeatColor = (value, max) => {
  if (!max) return HEATMAP_COLORS[0];
  const ratio = Math.min(value / max, 1);
  return HEATMAP_COLORS[Math.min(Math.floor(ratio * (HEATMAP_COLORS.length - 1)), HEATMAP_COLORS.length - 1)];
};

// ────────── Main Component ──────────
const InteractiveZoneChart = ({
  pitchEvents = [],        // Array of individual pitch events
  pitchLocations = [],     // Legacy location data (fallback)
  zones = {},              // Zone aggregate data for heatmap mode
  title = 'Interactive Zone Chart',
  perspective = 'batter',  // 'batter' or 'pitcher'
  playerHand = null,         // pitcher throws (R/L) or batter bats (R/L/S)
  size = 340,
  games = [],              // Available games for game filter dropdown
  onFilterChange,          // Callback when filters change
  onDrillDown,             // Callback ({pitchType, gameId, inning, gameDate, opponentName}) => navigate to At-Bat Explorer
  compact = false,         // Compact mode (less controls)
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── State ──
  const [mode, setMode] = useState('scatter'); // 'scatter' | 'heatmap'
  const [activePitchTypes, setActivePitchTypes] = useState(new Set()); // empty = all
  const [resultFilter, setResultFilter] = useState('all');
  const [abResultFilter, setAbResultFilter] = useState('all');
  const [handFilter, setHandFilter] = useState('all'); // 'all' | 'L' | 'R'
  const [selectedGame, setSelectedGame] = useState('all');
  const [showLastPitchOnly, setShowLastPitchOnly] = useState(false);
  const [hoveredPitch, setHoveredPitch] = useState(null);
  const [showFilters, setShowFilters] = useState(!compact);

  // ── Derive available pitch types from data ──
  const availablePitchTypes = useMemo(() => {
    const events = pitchEvents.length > 0 ? pitchEvents : pitchLocations;
    const counts = {};
    events.forEach(e => {
      const pt = e.pitchType || e.pt;
      if (pt) counts[pt] = (counts[pt] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count, name: PITCH_TYPE_NAMES[type] || type }));
  }, [pitchEvents, pitchLocations]);

  // ── Filter events ──
  const filteredEvents = useMemo(() => {
    let events = pitchEvents.length > 0
      ? pitchEvents.map(e => ({
          pitchType: e.pitchType || e.pt,
          pX: e.pX ?? e.plateX ?? null,
          pZ: e.pZ ?? e.plateZ ?? null,
          zone: e.zone || e.z,
          velocity: e.velocity || e.v,
          endSpeed: e.endSpeed || e.eV || null,
          result: e.result || e.r,
          opponentHand: e.opponentHand || e.h,
          gameId: e.gameId || e.g,
          gameDate: e.gameDate || e.gd,
          gameType: e.gameType || e.gt || null,
          opponent: e.opponent || e.opp,
          opponentName: e.opponentName || e.opn || null,
          isLastPitch: e.isLastPitch ?? e.last,
          atBatResult: e.atBatResult || e.abr || null,
          spinRate: e.spinRate || e.sp,
          horizBreak: e.horizBreak ?? e.hB ?? null,
          vertBreak: e.vertBreak ?? e.vB ?? null,
          extension: e.extension || e.ex || null,
          countBalls: e.countBalls ?? e.cB ?? null,
          countStrikes: e.countStrikes ?? e.cS ?? null,
          szT: e.szT ?? null,
          szB: e.szB ?? null,
          inning: e.inning || e.i,
        }))
      : pitchLocations.map(p => ({ ...p, pitchType: p.pitchType, pX: p.pX || p.plateX, pZ: p.pZ || p.plateZ }));

    // Pitch type filter
    if (activePitchTypes.size > 0) {
      events = events.filter(e => activePitchTypes.has(e.pitchType));
    }

    // Pitch result filter
    if (resultFilter !== 'all') {
      events = events.filter(e => e.result === resultFilter);
    }

    // At-bat result filter
    if (abResultFilter !== 'all') {
      if (abResultFilter === 'hit') {
        events = events.filter(e => e.atBatResult && ['single', 'double', 'triple', 'home_run', 'home run'].some(h => (e.atBatResult || '').toLowerCase().includes(h)));
      } else {
        events = events.filter(e => e.atBatResult && (e.atBatResult || '').toLowerCase().includes(abResultFilter.replace('_', ' ')));
      }
      // When filtering by at-bat result, only show last pitch of AB
      events = events.filter(e => e.isLastPitch);
    }

    // Handedness filter
    if (handFilter !== 'all') {
      events = events.filter(e => e.opponentHand === handFilter);
    }

    // Game filter
    if (selectedGame !== 'all') {
      events = events.filter(e => String(e.gameId) === selectedGame);
    }

    // Last pitch only toggle
    if (showLastPitchOnly) {
      events = events.filter(e => e.isLastPitch);
    }

    return events;
  }, [pitchEvents, pitchLocations, activePitchTypes, resultFilter, abResultFilter, handFilter, selectedGame, showLastPitchOnly]);

  // ── Zone Heatmap Data ──
  const heatmapData = useMemo(() => {
    const zoneAgg = {};
    filteredEvents.forEach(e => {
      if (e.zone == null) return;
      if (!zoneAgg[e.zone]) zoneAgg[e.zone] = { count: 0, hits: 0, swStr: 0 };
      zoneAgg[e.zone].count++;
      if (e.result === 'swstr') zoneAgg[e.zone].swStr++;
      if (e.atBatResult && ['single', 'double', 'triple', 'home_run'].some(h => (e.atBatResult || '').toLowerCase().includes(h))) {
        zoneAgg[e.zone].hits++;
      }
    });
    return zoneAgg;
  }, [filteredEvents]);

  const maxZoneCount = useMemo(() => Math.max(...Object.values(heatmapData).map(z => z.count), 1), [heatmapData]);

  // ── Toggle pitch type ──
  const togglePitchType = useCallback((type) => {
    setActivePitchTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) { next.delete(type); } else { next.add(type); }
      return next;
    });
  }, []);

  // ── Dynamic strike zone from per-batter Statcast szT / szB ──
  const dynamicZoneBounds = useMemo(() => {
    const allEvents = pitchEvents.length > 0 ? pitchEvents : pitchLocations;
    const szTs = allEvents.map(e => e.szT).filter(v => v != null && v > 0);
    const szBs = allEvents.map(e => e.szB).filter(v => v != null && v > 0);
    return {
      top: szTs.length > 0 ? +(szTs.reduce((a, b) => a + b, 0) / szTs.length).toFixed(3) : 3.5,
      bot: szBs.length > 0 ? +(szBs.reduce((a, b) => a + b, 0) / szBs.length).toFixed(3) : 1.5,
    };
  }, [pitchEvents, pitchLocations]);

  // ── SVG coordinates ──
  // Zone width: 17" plate + ball radius ≈ 20" = ±0.833 ft
  const zoneLeft = -0.833, zoneRight = 0.833;
  const zoneTop = dynamicZoneBounds.top, zoneBot = dynamicZoneBounds.bot;
  const viewLeft = -2.0, viewRight = 2.0, viewBot = 0, viewTop = 4.5;
  const svgHeight = size * 1.15;
  const mapX = (x) => ((x - viewLeft) / (viewRight - viewLeft)) * size;
  const mapY = (y) => ((viewTop - y) / (viewTop - viewBot)) * svgHeight;

  // ── Zone grid for heatmap ──
  const zoneGrid = useMemo(() => {
    const zW = (zoneRight - zoneLeft) / 3;
    const zH = (zoneTop - zoneBot) / 3;
    return [
      { zone: 1, x: zoneLeft, y: zoneTop - zH },
      { zone: 2, x: zoneLeft + zW, y: zoneTop - zH },
      { zone: 3, x: zoneLeft + 2 * zW, y: zoneTop - zH },
      { zone: 4, x: zoneLeft, y: zoneTop - 2 * zH },
      { zone: 5, x: zoneLeft + zW, y: zoneTop - 2 * zH },
      { zone: 6, x: zoneLeft + 2 * zW, y: zoneTop - 2 * zH },
      { zone: 7, x: zoneLeft, y: zoneBot },
      { zone: 8, x: zoneLeft + zW, y: zoneBot },
      { zone: 9, x: zoneLeft + 2 * zW, y: zoneBot },
    ];
  }, []);

  // ── Result color lookup for outcome rings ──
  const RESULT_RING_COLORS = {
    swstr: '#e74c3c',
    called: '#f39c12',
    foul: '#9b59b6',
    ball: '#3498db',
    inplay: '#2ecc71',
  };

  // ── Shape markers for AB results ──
  const AB_RESULT_MARKERS = {
    home_run: { symbol: '◆', stroke: '#FFD700', strokeWidth: 2.5 },
    strikeout: { symbol: 'K', stroke: '#e74c3c', strokeWidth: 2 },
    walk: { symbol: 'W', stroke: '#3498db', strokeWidth: 2 },
  };

  // ── Pretty result labels ──
  const RESULT_LABELS = {
    swstr: 'Swinging Strike', called: 'Called Strike', foul: 'Foul',
    ball: 'Ball', inplay: 'In Play',
  };

  // ── Render scatter dot ──
  const renderDot = (e, i) => {
    if (e.pX == null || e.pZ == null) return null;
    const color = PITCH_COLORS[e.pitchType] || '#888';
    const isHovered = hoveredPitch === i;

    // Outcome ring color
    const resultRing = RESULT_RING_COLORS[e.result] || (isDark ? '#0a0e1a' : '#fff');
    let dotRadius = 4;
    let opacity = 0.75;
    let ringWidth = 1.5;

    // Last pitch of at-bat gets special treatment
    if (e.isLastPitch && e.atBatResult) {
      dotRadius = 5.5;
      opacity = 0.9;
      ringWidth = 2;
    }

    // Home run gets gold ring override
    const isHR = e.isLastPitch && e.atBatResult &&
      ((e.atBatResult || '').toLowerCase().includes('home_run') ||
       (e.atBatResult || '').toLowerCase().includes('home run'));
    const finalStroke = isHovered ? '#fff' : (isHR ? '#FFD700' : resultRing);
    const finalStrokeWidth = isHovered ? 2.5 : (isHR ? 2.5 : ringWidth);

    const handleClick = () => {
      if (onDrillDown && e.gameId) {
        onDrillDown({ pitchType: e.pitchType, gameId: e.gameId, inning: e.inning, gameDate: e.gameDate, opponentName: e.opponentName });
      }
    };

    return (
      <g key={i}
        onMouseEnter={() => setHoveredPitch(i)}
        onMouseLeave={() => setHoveredPitch(null)}
        onClick={handleClick}
        style={{ cursor: onDrillDown ? 'pointer' : 'default' }}
      >
        <circle
          cx={mapX(e.pX)}
          cy={mapY(e.pZ)}
          r={isHovered ? dotRadius + 2 : dotRadius}
          fill={color}
          opacity={isHovered ? 1 : opacity}
          stroke={finalStroke}
          strokeWidth={finalStrokeWidth}
          style={{ transition: 'r 0.15s, opacity 0.15s' }}
        />
        {/* Gold diamond marker for home runs */}
        {isHR && !isHovered && (
          <text
            x={mapX(e.pX)}
            y={mapY(e.pZ) - dotRadius - 3}
            textAnchor="middle"
            fontSize={8}
            fill="#FFD700"
            style={{ pointerEvents: 'none' }}
          >◆</text>
        )}
      </g>
    );
  };

  // ── Render heatmap zone ──
  const renderHeatZone = (zg) => {
    const zW = (zoneRight - zoneLeft) / 3;
    const zH = (zoneTop - zoneBot) / 3;
    const data = heatmapData[zg.zone] || { count: 0, hits: 0, swStr: 0 };
    const bgColor = getHeatColor(data.count, maxZoneCount);

    return (
      <g key={`heatzone-${zg.zone}`}>
        <rect
          x={mapX(zg.x)}
          y={mapY(zg.y + zH)}
          width={mapX(zg.x + zW) - mapX(zg.x)}
          height={mapY(zg.y) - mapY(zg.y + zH)}
          fill={bgColor}
          stroke={isDark ? '#4a5080' : '#8892a8'}
          strokeWidth={0.5}
        />
        <text
          x={mapX(zg.x + zW / 2)}
          y={mapY(zg.y + zH / 2)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={isDark ? '#e0e0e0' : '#333'}
          fontSize={12}
          fontWeight="bold"
        >
          {data.count}
        </text>
      </g>
    );
  };

  // ── Tooltip content ──
  const tooltipContent = hoveredPitch != null && filteredEvents[hoveredPitch] ? (() => {
    const e = filteredEvents[hoveredPitch];
    const dateStr = e.gameDate ? new Date(e.gameDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
    const countStr = e.countBalls != null && e.countStrikes != null ? `${e.countBalls}-${e.countStrikes}` : null;
    const prettyResult = RESULT_LABELS[e.result] || e.result;
    const abResultPretty = e.atBatResult ? e.atBatResult.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
    return (
      <Paper sx={{ p: 1.2, maxWidth: 240, borderLeft: `3px solid ${PITCH_COLORS[e.pitchType] || '#888'}` }}>
        {/* Header: date + opponent */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.3 }}>
          {dateStr && <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>{dateStr}</Typography>}
          {e.opponent && <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>vs {e.opponent}</Typography>}
        </Box>
        {e.opponentName && (
          <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: '0.75rem', mb: 0.3 }}>
            {perspective === 'pitcher' ? 'Batter' : 'Pitcher'}: {e.opponentName.replace(/_/g, ' ')}
          </Typography>
        )}
        {/* Pitch info */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: PITCH_COLORS[e.pitchType], lineHeight: 1.2 }}>
          {PITCH_TYPE_NAMES[e.pitchType] || e.pitchType}
          {countStr && <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>({countStr})</Typography>}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
          {e.velocity > 0 && <Typography variant="caption">{e.velocity} mph</Typography>}
          {e.endSpeed > 0 && <Typography variant="caption" sx={{ opacity: 0.7 }}> → {e.endSpeed}</Typography>}
          {e.spinRate > 0 && <Typography variant="caption"> • {e.spinRate} rpm</Typography>}
        </Box>
        {(e.horizBreak != null || e.vertBreak != null) && (
          <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
            Break: {e.horizBreak != null ? `${e.horizBreak > 0 ? '+' : ''}${e.horizBreak}" H` : ''}
            {e.horizBreak != null && e.vertBreak != null ? ' / ' : ''}
            {e.vertBreak != null ? `${e.vertBreak > 0 ? '+' : ''}${e.vertBreak}" V` : ''}
          </Typography>
        )}
        {e.extension > 0 && <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>Ext: {e.extension} ft</Typography>}
        {/* Outcome */}
        <Typography variant="caption" display="block" sx={{ mt: 0.3, fontWeight: 600, color: RESULT_RING_COLORS[e.result] || 'text.primary' }}>
          {prettyResult}
        </Typography>
        {abResultPretty && (
          <Typography variant="caption" display="block" fontWeight="bold" sx={{ color: 'warning.main' }}>
            AB Result: {abResultPretty}
          </Typography>
        )}
        <Typography variant="caption" display="block" sx={{ opacity: 0.5, mt: 0.3, fontSize: '0.75rem' }}>
          Zone {e.zone} • Inn {e.inning} • vs {e.opponentHand}H{perspective === 'pitcher' ? 'B' : 'P'}
          {onDrillDown ? ' • Click to explore AB' : ''}
        </Typography>
      </Paper>
    );
  })() : null;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label={`${filteredEvents.length} pitches`} size="small" variant="outlined" />
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, v) => v && setMode(v)}
            size="small"
          >
            <ToggleButton value="scatter" sx={{ px: 1, py: 0.3 }}>
              <ScatterPlotIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
            <ToggleButton value="heatmap" sx={{ px: 1, py: 0.3 }}>
              <GridOnIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
          </ToggleButtonGroup>
          {compact && (
            <IconButton size="small" onClick={() => setShowFilters(f => !f)}>
              <FilterListIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* ── Filters ── */}
      <Fade in={showFilters}>
        <Box sx={{ display: showFilters ? 'block' : 'none' }}>
          {/* Pitch Type Chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {availablePitchTypes.map(({ type, count, name }) => {
              const active = activePitchTypes.size === 0 || activePitchTypes.has(type);
              return (
                <Chip
                  key={type}
                  label={`${name} (${count})`}
                  size="small"
                  onClick={() => togglePitchType(type)}
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    bgcolor: active ? alpha(PITCH_COLORS[type] || '#888', 0.2) : 'transparent',
                    borderColor: PITCH_COLORS[type] || '#888',
                    color: active ? (isDark ? '#fff' : '#333') : theme.palette.text.disabled,
                    fontWeight: active ? 700 : 400,
                    opacity: active ? 1 : 0.5,
                    '&:hover': { opacity: 1 },
                  }}
                  variant="outlined"
                />
              );
            })}
          </Box>

          {/* Filter Row */}
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {/* Handedness */}
            <ToggleButtonGroup
              value={handFilter}
              exclusive
              onChange={(e, v) => v && setHandFilter(v)}
              size="small"
            >
              <ToggleButton value="all" sx={{ px: 1, py: 0.2, fontSize: '0.75rem' }}>All</ToggleButton>
              <ToggleButton value="L" sx={{ px: 1, py: 0.2, fontSize: '0.75rem' }}>vs LH</ToggleButton>
              <ToggleButton value="R" sx={{ px: 1, py: 0.2, fontSize: '0.75rem' }}>vs RH</ToggleButton>
            </ToggleButtonGroup>

            {/* Pitch Result */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Result</InputLabel>
              <Select
                value={resultFilter}
                label="Result"
                onChange={(e) => setResultFilter(e.target.value)}
                sx={{ fontSize: '0.75rem', height: 32 }}
              >
                {RESULT_FILTERS.map(f => (
                  <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.75rem' }}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* At-Bat Result */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>AB Result</InputLabel>
              <Select
                value={abResultFilter}
                label="AB Result"
                onChange={(e) => setAbResultFilter(e.target.value)}
                sx={{ fontSize: '0.75rem', height: 32 }}
              >
                {AB_RESULT_FILTERS.map(f => (
                  <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.75rem' }}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Game filter */}
            {games.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Game</InputLabel>
                <Select
                  value={selectedGame}
                  label="Game"
                  onChange={(e) => setSelectedGame(e.target.value)}
                  sx={{ fontSize: '0.75rem', height: 32 }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Games</MenuItem>
                  {games.map(g => (
                    <MenuItem key={g.gameId} value={String(g.gameId)} sx={{ fontSize: '0.75rem' }}>
                      {g.date} vs {g.opponent}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* Extra toggles */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={showLastPitchOnly} onChange={(e) => setShowLastPitchOnly(e.target.checked)} />}
              label={<Typography variant="caption">Last pitch only</Typography>}
            />
          </Stack>
        </Box>
      </Fade>

      {/* ── SVG Zone Chart ── */}
      <Tooltip
        open={hoveredPitch != null}
        title={tooltipContent || ''}
        placement="right"
        followCursor
        PopperProps={{ style: { pointerEvents: 'none' } }}
      >
        <svg width="100%" height="auto" viewBox={`0 0 ${size} ${svgHeight}`} style={{ display: 'block', margin: '0 auto', maxWidth: size }}>
          {/* Background */}
          <rect width={size} height={svgHeight} fill={isDark ? '#151a2e' : '#f5f7fa'} rx={8} />

          {/* Strike zone box */}
          <rect
            x={mapX(zoneLeft)} y={mapY(zoneTop)}
            width={mapX(zoneRight) - mapX(zoneLeft)}
            height={mapY(zoneBot) - mapY(zoneTop)}
            fill="none" stroke={isDark ? '#4a5080' : '#8892a8'} strokeWidth={2}
          />

          {/* Zone grid lines */}
          {[1, 2].map(i => {
            const xFrac = zoneLeft + (i / 3) * (zoneRight - zoneLeft);
            const yFrac = zoneBot + (i / 3) * (zoneTop - zoneBot);
            return (
              <React.Fragment key={i}>
                <line x1={mapX(xFrac)} y1={mapY(zoneTop)} x2={mapX(xFrac)} y2={mapY(zoneBot)}
                  stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
                <line x1={mapX(zoneLeft)} y1={mapY(yFrac)} x2={mapX(zoneRight)} y2={mapY(yFrac)}
                  stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
              </React.Fragment>
            );
          })}

          {/* Home plate */}
          <polygon
            points={`${mapX(-0.7083)},${mapY(0.5)} ${mapX(-0.7083)},${mapY(0.25)} ${mapX(0)},${mapY(0)} ${mapX(0.7083)},${mapY(0.25)} ${mapX(0.7083)},${mapY(0.5)}`}
            fill={isDark ? '#2a3050' : '#e0e4ec'} stroke={isDark ? '#3a4060' : '#b0b8c8'} strokeWidth={1}
          />



          {/* HEATMAP MODE */}
          {mode === 'heatmap' && zoneGrid.map(zg => renderHeatZone(zg))}

          {/* SCATTER MODE */}
          {mode === 'scatter' && filteredEvents.map((e, i) => renderDot(e, i))}

          {/* Empty state */}
          {filteredEvents.length === 0 && (
            <text x={size / 2} y={svgHeight / 2} textAnchor="middle" fill={isDark ? '#666' : '#999'} fontSize={13}>
              No pitches match filters
            </text>
          )}
        </svg>
      </Tooltip>

      {/* ── Legend (scatter mode) ── */}
      {mode === 'scatter' && (
        <Box sx={{ mt: 1 }}>
          {/* Pitch Type Legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
            {availablePitchTypes
              .filter(({ type }) => activePitchTypes.size === 0 || activePitchTypes.has(type))
              .slice(0, 8)
              .map(({ type, count }) => (
                <Typography key={type} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.75rem', color: 'text.secondary' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[type] || '#888' }} />
                  {PITCH_TYPE_NAMES[type] || type} ({filteredEvents.filter(e => e.pitchType === type).length})
                </Typography>
              ))}
          </Box>

          {/* Outcome Ring Legend */}
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 0.8, justifyContent: 'center', mt: 0.8,
            pt: 0.8, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', opacity: 0.5, mr: 0.3, alignSelf: 'center' }}>
              Ring =
            </Typography>
            {RESULT_FILTERS.filter(r => r.key !== 'all').map(r => {
              const cnt = filteredEvents.filter(e => e.result === r.key).length;
              return (
                <Typography key={r.key} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.75rem', color: 'text.secondary' }}>
                  <Box sx={{
                    width: 10, height: 10, borderRadius: '50%',
                    border: `2px solid ${r.color}`,
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  }} />
                  {r.label} ({cnt})
                </Typography>
              );
            })}
            {/* HR marker */}
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.75rem', color: 'text.secondary' }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%',
                border: '2.5px solid #FFD700',
                bgcolor: isDark ? 'rgba(255,215,0,0.15)' : 'rgba(255,215,0,0.1)',
              }} />
              <span style={{ color: '#FFD700' }}>◆</span> HR
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Heatmap legend ── */}
      {mode === 'heatmap' && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>Cold</Typography>
          <Box sx={{ display: 'flex', height: 12, borderRadius: 1, overflow: 'hidden' }}>
            {HEATMAP_COLORS.map((c, i) => (
              <Box key={i} sx={{ width: 18, height: '100%', backgroundColor: c }} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>Hot</Typography>
        </Box>
      )}

      {/* ── Summary stats for filtered set ── */}
      {filteredEvents.length > 0 && (
        <Box sx={{
          mt: 1.5, p: 1, borderRadius: 1,
          bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
          display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center',
        }}>
          {(() => {
            const total = filteredEvents.length;
            const inZone = filteredEvents.filter(e => e.zone >= 1 && e.zone <= 9).length;
            const swStr = filteredEvents.filter(e => e.result === 'swstr').length;
            const called = filteredEvents.filter(e => e.result === 'called').length;
            const balls = filteredEvents.filter(e => e.result === 'ball').length;
            const inPlay = filteredEvents.filter(e => e.result === 'inplay').length;
            const velos = filteredEvents.filter(e => e.velocity > 0).map(e => e.velocity);
            const avgVelo = velos.length > 0 ? (velos.reduce((a, b) => a + b, 0) / velos.length).toFixed(1) : '-';

            return [
              { label: 'Total', value: total },
              { label: 'Zone%', value: total > 0 ? `${((inZone / total) * 100).toFixed(1)}%` : '-' },
              { label: 'SwStr%', value: total > 0 ? `${((swStr / total) * 100).toFixed(1)}%` : '-' },
              { label: 'CSW%', value: total > 0 ? `${(((swStr + called) / total) * 100).toFixed(1)}%` : '-' },
              { label: 'Avg Velo', value: avgVelo },
              { label: 'In Play', value: inPlay },
            ].map(s => (
              <Box key={s.label} sx={{ textAlign: 'center', minWidth: 50 }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', opacity: 0.7, display: 'block' }}>{s.label}</Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{s.value}</Typography>
              </Box>
            ));
          })()}
        </Box>
      )}
    </Box>
  );
};

export default InteractiveZoneChart;
